import { signatureManifestationLines } from 'libs.consent.common';
/** Wall-clock components of an ISO timestamp, ignoring any trailing offset. */
const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;
/**
 * `locale` must be the document's locale, passed explicitly. Leaving it
 * undefined falls back to the JS runtime default, which is the browser/OS
 * locale - so the same signature record renders a different date on the
 * archived PDF depending on how the signer's machine happens to be configured.
 */
export function formatOverlayDate(raw, timeZone, locale) {
    try {
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
            // 24-hour per demo feedback. It also keeps a locale-varying token off the
            // record: `hour12: true` renders `2:32 PM` under `en-US` and `02:32 pm`
            // under `en-GB`, where `14:32` is the same in both. `timeZoneName` cannot
            // sit alongside `dateStyle` (Intl throws "Can't set option timeZoneName
            // when dateStyle is used"), but `hour12` can - it's the only clock-format
            // knob available here, so the zone stays exactly as passed in (the IANA
            // identifier), unaffected.
            hour12: false,
            ...(timeZone ? { timeZone } : {}),
        }).format(new Date(raw));
    }
    catch {
        return raw;
    }
}
/**
 * Numeric UTC offset designator, matching `Intl`'s `shortOffset` convention:
 * `GMT+5:30`, `GMT-4`, `GMT+0`.
 *
 * Derived from the offset the device attested, not from the zone plus the
 * instant. The wall-clock digits are read straight out of the stored string and
 * deliberately never re-projected, so there is no trustworthy instant to hand
 * `Intl` here - and the stored offset is what the signer's device actually
 * reported, which is the thing being attested.
 *
 * A numeric offset rather than a zone abbreviation because the abbreviation is
 * locale-dependent and cannot be relied on: `timeZoneName: 'short'` yields
 * `IST` only when the runtime locale matches the zone's home region, and
 * degrades to a `GMT+5:30`-style offset everywhere else. Since the document
 * locale is independent of the signer's zone by design, an abbreviation would
 * render inconsistently between two signers of the same document.
 */
function offsetDesignator(utcOffset) {
    const trimmed = (utcOffset || '').trim();
    if (!trimmed) {
        return undefined;
    }
    if (trimmed === 'Z') {
        return 'GMT+0';
    }
    const parts = /^([+-])(\d{2}):?(\d{2})$/.exec(trimmed);
    if (!parts) {
        return undefined;
    }
    const [, sign, hours, minutes] = parts;
    return +minutes === 0 ? `GMT${sign}${+hours}` : `GMT${sign}${+hours}:${minutes}`;
}
/** A zone string only earns a place on the record if `Intl` can resolve it. */
function resolvableZone(zone) {
    if (!zone) {
        return undefined;
    }
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return zone;
    }
    catch {
        return undefined;
    }
}
/**
 * IANA zone when the record captured one Intl can resolve; numeric offset only
 * when no resolvable zone was stored. The offset stays on the audit record
 * (`deviceUtcOffset`); putting it on the stamp next to a zone is redundant and
 * disagrees with the wall-clock display contract.
 */
function wallClockDesignator(zone, utcOffset) {
    return resolvableZone(zone) || offsetDesignator(utcOffset);
}
/**
 * Render an attested wall clock exactly as the device reported it.
 *
 * The stored string carries its own offset, so it must not be re-projected
 * into the attested zone: when the two disagree - a `+00:00` stamp against an
 * `Asia/Calcutta` zone - re-projecting silently moves the displayed time hours
 * away from the clock the signer actually saw. Reading the wall-clock fields
 * straight out of the string and formatting them as if they were UTC keeps the
 * digits intact while still localizing the format.
 */
function formatAttestedWallClock(raw, locale) {
    const parts = WALL_CLOCK_RE.exec(raw);
    if (!parts) {
        return undefined;
    }
    const [, year, month, day, hour, minute, second] = parts;
    const asUtc = new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, second ? +second : 0));
    if (Number.isNaN(asUtc.getTime())) {
        return undefined;
    }
    return formatOverlayDate(asUtc.toISOString(), 'UTC', locale);
}
/**
 * Device-attested local time plus its zone when the record actually carries
 * one. Anything else - no attested wall clock, a clock the record says came
 * from the server, an unparseable stamp - renders the server instant as UTC
 * with an explicit designator, never a server time wearing a device zone.
 */
export function formatSignatureTimestamp(clock, locale) {
    const attestedByDevice = clock.clockSource !== 'server' && !!clock.deviceLocalTime;
    const wallClock = attestedByDevice ? formatAttestedWallClock(clock.deviceLocalTime, locale) : undefined;
    if (wallClock) {
        const designator = wallClockDesignator(clock.deviceIanaZone, clock.deviceUtcOffset);
        return designator ? `${wallClock} (${designator})` : wallClock;
    }
    if (!clock.date) {
        return undefined;
    }
    return `${formatOverlayDate(clock.date, 'UTC', locale)} UTC`;
}
/**
 * Manifestation renders bare, matching the legacy signed partial: the authored
 * geometry of a signature box assumes no `Signed by ` / `Date: ` / `ID: `
 * prefix, and the extra width pushes the date line into a wrap it cannot
 * afford.
 */
export function signatureOverlayLines(field, locale) {
    return signatureManifestationLines({
        signedBy: field.name,
        meaning: field.meaning,
        attestation: field.attestation,
        date: formatSignatureTimestamp(field, locale),
        id: field.id,
    });
}
